import { HostCapabilities, VisibilitySample } from '../visibility'

// A threshold per 2% of coverage: the iframe needs enough resolution to see a badge cross 10% into
// the viewport and 50% seen, without an IO callback per pixel.
const THRESHOLDS = Array.from({ length: 51 }, (_, i) => i / 50)
const TICK_MS = 1000
const PROBE_COLUMNS = 3
const PROBE_ROWS = 5

export function getHostCapabilities(): HostCapabilities {
  return {
    intersectionObserver: typeof IntersectionObserver !== 'undefined',
    elementFromPoint: typeof document.elementFromPoint === 'function',
    sendBeacon: typeof navigator.sendBeacon === 'function',
    timeOrigin: performance.timeOrigin,
  }
}

export function probeOccludedRatio(iframe: HTMLIFrameElement, rect: DOMRectReadOnly): number {
  if (rect.width <= 0 || rect.height <= 0) return 0

  let occluded = 0
  for (let c = 0; c < PROBE_COLUMNS; c++) {
    for (let r = 0; r < PROBE_ROWS; r++) {
      const x = rect.left + ((c + 0.5) / PROBE_COLUMNS) * rect.width
      const y = rect.top + ((r + 0.5) / PROBE_ROWS) * rect.height
      if (document.elementFromPoint(x, y) !== iframe) occluded++
    }
  }
  return occluded / (PROBE_COLUMNS * PROBE_ROWS)
}

export interface VisibilityTracker {
  stop: () => void
}

/**
 * Observes the iframe with IntersectionObserver v1 and reports raw samples. No rootMargin and no
 * selector knowledge on purpose: occlusion by host chrome (sticky headers, cookie banners, chat
 * bubbles) is measured generically by hit-testing points inside the intersection instead. Returns
 * null when the host has no IntersectionObserver, so the caller can report the gap once.
 */
export function createVisibilityTracker(
  iframe: HTMLIFrameElement,
  onSample: (sample: VisibilitySample) => void
): VisibilityTracker | null {
  if (!getHostCapabilities().intersectionObserver) return null

  let lastEntry: IntersectionObserverEntry | null = null
  let rafId: number | null = null
  let tickId: ReturnType<typeof setInterval> | null = null

  const emit = () => {
    const entry = lastEntry
    if (!entry) return

    const rect = entry.intersectionRect
    onSample({
      at: performance.now(),
      pageVisible: document.visibilityState === 'visible',
      viewportHeight: window.innerHeight,
      iframeHeight: entry.boundingClientRect.height,
      intersectionHeight: rect.height,
      occludedRatio: rect.height > 0 ? probeOccludedRatio(iframe, rect) : 0,
    })
  }

  const schedule = () => {
    if (rafId !== null) return
    rafId = requestAnimationFrame(() => {
      rafId = null
      emit()
    })
  }

  const setTicking = (on: boolean) => {
    if (on && tickId === null) tickId = setInterval(emit, TICK_MS)
    if (!on && tickId !== null) {
      clearInterval(tickId)
      tickId = null
    }
  }

  // Nothing observable changes while the host tab is hidden, so the tick pauses — but a sample is
  // still emitted on each visibility transition, and it resumes on visible if still intersecting.
  const shouldTick = () => document.visibilityState === 'visible' && !!lastEntry?.isIntersecting

  const observer = new IntersectionObserver(
    entries => {
      lastEntry = entries[entries.length - 1] ?? null
      // The tick re-probes occlusion, which can change with no IO callback (a header appears on
      // scroll); intersection itself cannot, so keeping `lastEntry` between ticks is safe.
      setTicking(shouldTick())
      schedule()
    },
    { threshold: THRESHOLDS }
  )
  observer.observe(iframe)

  const onVisibilityChange = () => {
    setTicking(shouldTick())
    schedule()
  }
  document.addEventListener('visibilitychange', onVisibilityChange)

  return {
    stop: () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibilityChange)
      setTicking(false)
      if (rafId !== null) cancelAnimationFrame(rafId)
    },
  }
}
